import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { toast } from "sonner";
import SEO, { organizationSchema, CITY_BRANCHES, localBusinessSchema } from "../../lib/seo";
import { useIsMenuEnabled } from "../../lib/sitePages";
import { useNavMenu } from "../../lib/navMenus";
import {
  ArrowRight, ShieldCheck, Truck, Recycle, Trophy, Sparkle, Globe,
  ChartLineUp, Storefront, Users, MapTrifold, QrCode, CheckCircle,
  GraduationCap, BuildingOffice, EnvelopeSimple, Phone,
  MapPin, LinkedinLogo, InstagramLogo, TwitterLogo, YoutubeLogo,
  CaretDown, Leaf, Briefcase,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Motion variants ---
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } },
};

// --- Token classes ---
const BTN_GREEN = "inline-flex items-center justify-center gap-2 bg-[#2D5A27] text-white hover:bg-[#1B3B17] transition-all duration-300 rounded-full px-6 py-3 font-medium shadow-lg shadow-green-900/15";
const BTN_RED = "inline-flex items-center justify-center gap-2 bg-[#E63946] text-white hover:bg-[#c1303c] transition-all duration-300 rounded-full px-6 py-3 font-medium shadow-lg shadow-red-900/15";
const BTN_GHOST = "inline-flex items-center justify-center gap-2 bg-white/70 backdrop-blur-md border border-slate-200 text-slate-800 hover:bg-white transition-all duration-300 rounded-full px-6 py-3 font-medium";
const GLASS = "bg-white/70 backdrop-blur-xl backdrop-saturate-150 border border-white/40 shadow-[0_8px_32px_rgba(45,90,39,0.08)]";
const SECTION = "px-6 md:px-12 lg:px-24 py-20 lg:py-32";
const H1 = "font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-medium text-slate-900";
const H2 = "font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05] font-medium text-slate-900";
const EYEBROW = "text-xs uppercase tracking-[0.24em] font-medium text-[#2D5A27]";
const LEAD = "text-base sm:text-lg leading-relaxed text-slate-600";

export default function HomePage() {
  const [branding, setBranding] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    axios.get(`${API}/public/branding`).then((r) => setBranding(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="bg-white text-slate-900 antialiased overflow-x-hidden" data-testid="homepage">
      <SEO title="Biomedical waste collection Kerala · Homes, clinics, LSGDs"
            description="Rescigo — Kerala's CPCB-compliant doorstep biomedical, diaper, sanitary & expired-medicine waste collection service. Trivandrum, Kochi, Kottayam and 12+ cities."
            path="/"
            keywords="biomedical waste collection Kerala, biomedical waste collection Trivandrum, biomedical waste collection Kochi, diaper waste disposal Kerala, sanitary napkin disposal Kerala, expired medicine disposal, home biomedical waste, CPCB authorised waste collector Kerala"
            jsonLd={[organizationSchema(), ...CITY_BRANCHES.map(localBusinessSchema)]}/>
      <TopNav scrolled={scrolled} branding={branding} />
      <Hero branding={branding} />
      <ScrollStory branding={branding} />
      <SectionA branding={branding} />
      <SectionC branding={branding} />
      <SectionD branding={branding} />
      <SectionE branding={branding} />
      <SectionH branding={branding} />
      <SectionI branding={branding} />
      <Footer branding={branding} />
    </div>
  );
}

// =============== Top Nav ===============
const TopNav = ({ scrolled, branding }) => {
  const [open, setOpen] = useState(false);
  const [solOpen, setSolOpen] = useState(false);
  const [resOpen, setResOpen] = useState(false);
  const mainItems = useNavMenu("main");
  const rawSigninItems = useNavMenu("signin");
  // Never expose `/adentry` in any sign-in menu — admin console is
  // direct-entry only.
  const signinItems = useMemo(
    () => rawSigninItems.filter(it => it.path !== "/adentry"),
    [rawSigninItems],
  );
  // Each nav entry respects the superadmin "Show in menu" toggle.
  const menuOn = {
    "/for-government": useIsMenuEnabled("/for-government"),
    "/for-companies": useIsMenuEnabled("/for-companies"),
    "/for-healthcare": useIsMenuEnabled("/for-healthcare"),
    "/newsroom": useIsMenuEnabled("/newsroom"),
    "/white-papers": useIsMenuEnabled("/white-papers"),
    "/csr-sustainability": useIsMenuEnabled("/csr-sustainability"),
    "/security-compliance": useIsMenuEnabled("/security-compliance"),
    "/investors": useIsMenuEnabled("/investors"),
  };
  const solutionsOn = menuOn["/for-government"] || menuOn["/for-companies"] || menuOn["/for-healthcare"];
  const resourcesOn = menuOn["/newsroom"] || menuOn["/white-papers"] || menuOn["/csr-sustainability"] || menuOn["/security-compliance"] || menuOn["/investors"];
  return (
    <header
      data-scrolled={scrolled}
      data-testid="top-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-12 h-16 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2.5">
          <img
            src={branding?.logo_url || "/brand/rescigo-logo.png"}
            alt="Rescigo Health"
            className="h-9 w-auto"
            data-testid="nav-logo"
          />
        </Link>
        <nav className="hidden md:flex items-center gap-7 ml-4 text-sm text-slate-600">
          {solutionsOn && (
            <div className="relative" onMouseEnter={() => setSolOpen(true)} onMouseLeave={() => setSolOpen(false)}>
              <button data-testid="nav-solutions" className="inline-flex items-center gap-1 hover:text-slate-900 h-16">
                Solutions <CaretDown size={11} weight="bold"/>
              </button>
              {solOpen && (
                <div className="absolute top-14 left-0 w-64 bg-white rounded-xl shadow-xl ring-1 ring-slate-200/60 p-2">
                  {menuOn["/for-government"] && <NavSubItem to="/for-government" title="For Government" desc="Regulatory intelligence for waste governance" testid="nav-sub-gov"/>}
                  {menuOn["/for-companies"] && <NavSubItem to="/for-companies" title="For Companies" desc="ESG, compliance & waste intelligence" testid="nav-sub-companies"/>}
                  {menuOn["/for-healthcare"] && <NavSubItem to="/for-healthcare" title="For Healthcare" desc="BMW-compliant biomedical infrastructure" testid="nav-sub-healthcare"/>}
                </div>
              )}
            </div>
          )}
          {resourcesOn && (
            <div className="relative" onMouseEnter={() => setResOpen(true)} onMouseLeave={() => setResOpen(false)}>
              <button data-testid="nav-resources" className="inline-flex items-center gap-1 hover:text-slate-900 h-16">
                Resources <CaretDown size={11} weight="bold"/>
              </button>
              {resOpen && (
                <div className="absolute top-14 left-0 w-64 bg-white rounded-xl shadow-xl ring-1 ring-slate-200/60 p-2">
                  {menuOn["/newsroom"] && <NavSubItem to="/newsroom" title="Newsroom" desc="Press releases & company updates"/>}
                  {menuOn["/white-papers"] && <NavSubItem to="/white-papers" title="White Papers" desc="Research & policy frameworks"/>}
                  {menuOn["/csr-sustainability"] && <NavSubItem to="/csr-sustainability" title="CSR & Sustainability" desc="Impact metrics & SDG alignment"/>}
                  {menuOn["/security-compliance"] && <NavSubItem to="/security-compliance" title="Security & Compliance" desc="Enterprise-grade data integrity"/>}
                  {menuOn["/investors"] && <NavSubItem to="/investors" title="Investor Relations" desc="Market opportunity & scale"/>}
                </div>
              )}
            </div>
          )}
          {mainItems.map(it => (
            <Link key={it.path} to={it.path}
                  data-testid={`nav-main-${it.path.replace(/[/]/g,'-')}`}
                  className="hover:text-slate-900 transition-colors">{it.label}</Link>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {signinItems.length > 0 && (
            <div className="relative hidden sm:block">
              <button
                onClick={() => setOpen((v) => !v)}
                data-testid="nav-login-trigger"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900 px-3 py-2 rounded-full hover:bg-slate-100 transition"
              >
                Sign in <CaretDown size={12} weight="bold" />
              </button>
              {open && (
                <div onMouseLeave={() => setOpen(false)} className={`absolute right-0 mt-2 w-56 rounded-xl ${GLASS} p-2 z-10`}>
                  {signinItems.map(it => (
                    <Link key={it.path} to={it.path}
                           data-testid={`signin-link-${it.path.replace(/[/]/g,'-')}`}
                           className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-sm text-slate-700">
                      {it.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          <Link to="/register" data-testid="nav-cta-book" className={`${BTN_GREEN} h-10 text-sm`}>
            Book service <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </header>
  );
};

const NavSubItem = ({ to, title, desc, testid }) => (
  <Link to={to} data-testid={testid}
        className="block px-3 py-2.5 rounded-lg hover:bg-slate-50 transition">
    <div className="text-sm font-semibold text-slate-900">{title}</div>
    <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
  </Link>
);

const LoginRow = ({ to, label, Icon, restricted, testid }) => (
  <Link to={to} data-testid={testid}
        className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm hover:bg-slate-100 transition ${restricted ? "text-slate-700" : "text-slate-800"}`}>
    <Icon size={14} weight="duotone" />
    <span className="flex-1">{label}</span>
    {restricted && <span className="text-[9px] uppercase tracking-[0.18em] text-slate-400">Restricted</span>}
  </Link>
);

// =============== Hero ===============
const Hero = ({ branding }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <section ref={ref} className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden" data-testid="hero">
      {/* Background blobs */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[1100px] h-[600px] rounded-full bg-[#E8F0E7] blur-3xl opacity-60" />
        <div className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full bg-[#FCD9DD] blur-3xl opacity-40" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(45,90,39,0.08),transparent_60%)]" />
      </div>

      <motion.div style={{ y, opacity }} className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2D5A27]/20 bg-white/60 backdrop-blur text-xs font-medium text-[#1B3B17] mb-6">
            <Sparkle size={12} weight="fill" color="#E63946" />
            ISO 14001 · CPCB-compliant · GDPR-aware
          </motion.div>
          <motion.h1 variants={fadeUp} className={H1}>
            India's <span className="text-[#E63946]">Smart Waste</span><br />
            Management <span className="italic font-light">Infrastructure</span>
          </motion.h1>
          <motion.p variants={fadeUp} className={`${LEAD} mt-6 max-w-2xl mx-auto`}>
            End-to-end QR-tracked collection, route automation, disposal compliance, and sustainability intelligence — purpose-built for a national-grade smart waste operations ecosystem.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" data-testid="hero-book-service-btn" className={`${BTN_GREEN}`}>
              <Truck size={16} weight="fill" /> Book Service
            </Link>
            <Link to="/register" data-testid="hero-register-btn" className={BTN_GHOST}>
              <Users size={16} /> Register
            </Link>
            <a href="#franchise" data-testid="hero-partner-btn" className={`${BTN_RED}`}>
              <Trophy size={16} weight="fill" /> Become Partner
            </a>
            <Link to="/government" data-testid="hero-gov-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-700 underline-offset-4 hover:underline px-3 py-2">
              <BuildingOffice size={14} /> Government access <ArrowRight size={12} weight="bold" />
            </Link>
          </motion.div>
        </motion.div>

        {/* Trust strip */}
        <motion.div variants={fadeIn} initial="hidden" animate="visible"
                    className="mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs uppercase tracking-[0.2em] text-slate-400">
          {["Trivandrum Corp.", "BMW Hospital", "TKM College", "AmbalSagara Mart", "Kerala Health Dept", "Greens Nursery"]
            .map((n) => <span key={n} className="font-medium">{n}</span>)}
        </motion.div>
      </motion.div>
    </section>
  );
};

// =============== Scroll story (5 steps) ===============
const ScrollStory = ({ branding }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const steps = [
    { I: QrCode, title: "Customer books service", desc: "Phone-OTP login · zone-aware schedule · auto-priced package" },
    { I: Recycle, title: "QR bag packed", desc: "Sequential RB##### codes · category-locked · weight tracked" },
    { I: Truck, title: "Waste enters truck", desc: "Field staff scans · GPS captured · live route progress" },
    { I: MapTrifold, title: "Route optimized", desc: "Distance/density model · sequence-locked · admin override" },
    { I: ShieldCheck, title: "Disposal certified", desc: "CPCB chain-of-custody · auto-issued certificates" },
  ];
  return (
    <section ref={ref} className={`${SECTION} bg-gradient-to-b from-white via-[#F7FAF6] to-white`} data-testid="scroll-story">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className={`${H2}`}>{branding?.flow_title || "Five steps. Fully traceable."}</h2>
        </div>
        <div className="relative">
          <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#2D5A27]/30 to-transparent" />
          <motion.div className="absolute left-1/2 -translate-x-1/2 top-0 w-1.5 bg-gradient-to-b from-[#E63946] to-[#2D5A27] rounded-full"
                       style={{ height: useTransform(scrollYProgress, [0, 1], ["0%", "100%"]) }} />
          <div className="space-y-10 lg:space-y-16">
            {steps.map((s, i) => (
              <StoryStep key={i} idx={i} {...s} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const StoryStep = ({ idx, I, title, desc }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20% 0px" });
  const left = idx % 2 === 0;
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp}
                className={`grid lg:grid-cols-2 gap-6 items-center ${left ? "" : "lg:[direction:rtl]"}`}>
      <div className={`${left ? "lg:text-right" : "lg:text-left"} [direction:ltr]`}>
        <div className={`inline-flex items-center gap-2 ${left ? "lg:flex-row-reverse" : ""}`}>
          <span className="text-xs font-mono text-slate-400">0{idx + 1}</span>
          <span className={EYEBROW}>step</span>
        </div>
        <h3 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium mt-2">{title}</h3>
        <p className="text-slate-600 mt-2 max-w-md inline-block">{desc}</p>
      </div>
      <div className="flex items-center justify-center [direction:ltr]">
        <div className={`w-28 h-28 rounded-2xl ${GLASS} flex items-center justify-center relative`}>
          <I size={36} color="#2D5A27" weight="duotone" />
          <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#E63946] text-white text-xs font-bold flex items-center justify-center">{idx + 1}</div>
        </div>
      </div>
    </motion.div>
  );
};

// =============== Section A — Smart Service Flow ===============
const SectionA = ({ branding }) => {
  const tiles = [
    { I: QrCode, label: "QR-tracked bags", desc: "Sequential codes, category locks, weight + GPS at scan", span: "lg:col-span-5 lg:row-span-2" },
    { I: MapTrifold, label: "Route automation", desc: "Sequence-locked stops, live nav, admin override", span: "lg:col-span-4" },
    { I: ShieldCheck, label: "Compliance trail", desc: "CPCB chain-of-custody · digital certificates", span: "lg:col-span-3" },
    { I: ChartLineUp, label: "Live operations", desc: "Real-time KPIs · fraud detection · auto-alerts", span: "lg:col-span-3" },
    { I: Storefront, label: "Storefront billing", desc: "Auto-invoices · UPI · WhatsApp delivery", span: "lg:col-span-4" },
  ];
  return (
    <section id="solutions" className={`${SECTION} bg-white`} data-testid="section-a">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-10 mb-14 items-end">
          <div>
            <h2 className={`${H2}`}>{branding?.section_a_title || "Smart Service Flow"}</h2>
          </div>
          <p className={LEAD}>{branding?.section_a_body || "From the moment a customer books to the moment the certificate hits their inbox — every gram is observed, priced, and disposed under audit."}</p>
        </div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-5 auto-rows-[200px]">
          {tiles.map((t, i) => (
            <motion.div key={i} variants={fadeUp}
                        className={`${t.span} group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-[#F7FAF6] p-6 hover:shadow-xl hover:shadow-green-900/5 hover:-translate-y-1 transition-all duration-500`}>
              <t.I size={28} color="#2D5A27" weight="duotone" />
              <div className="mt-auto">
                <div className="font-heading text-xl tracking-tight font-medium text-slate-900 mt-4">{t.label}</div>
                <div className="text-sm text-slate-600 mt-1">{t.desc}</div>
              </div>
              <div className="absolute -bottom-12 -right-12 w-40 h-40 rounded-full bg-[#2D5A27]/5 group-hover:bg-[#2D5A27]/10 transition-all" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// =============== Section C — Franchise (no calculator) ===============
const SectionC = ({ branding }) => {
  const bullets = (branding?.section_c_bullets && branding.section_c_bullets.length)
    ? branding.section_c_bullets
    : [
      "Exclusive zonal rights · LSGD-locked",
      "10% partner commission · weekly payouts",
      "Co-branded marketing kit + sales training",
      "Live ops dashboard · GST + finance modules",
      "Compliance + insurance handled centrally",
    ];
  return (
    <section id="franchise" className={`${SECTION} bg-white`} data-testid="section-c">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <h2 className={`${H2}`}>{branding?.section_c_title || "Own your territory."}</h2>
          <p className={`${LEAD} mt-5 max-w-xl`}>{branding?.section_c_body || "Operate Rescigo as a co-branded franchise. We handle technology, compliance, and supplier rails. You operate the field network and own the customer relationship."}</p>
          <ul className="mt-7 space-y-3">
            {bullets.map((x, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700"><CheckCircle size={16} className="text-[#2D5A27] mt-0.5 shrink-0" weight="fill" />{x}</li>
            ))}
          </ul>
          <a href="#contact" data-testid="franchise-cta" className={`${BTN_RED} mt-8`}>{branding?.section_c_cta || "Apply for franchise"} <ArrowRight size={14} weight="bold" /></a>
        </div>
        <div className="lg:col-span-5">
          <div className={`${GLASS} rounded-3xl p-7 lg:p-8 relative overflow-hidden`}>
            <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[#E63946]/10 blur-2xl" />
            <div className="absolute -bottom-12 -left-12 w-44 h-44 rounded-full bg-[#2D5A27]/10 blur-2xl" />
            <div className="relative">
              <div className="flex items-center gap-2 text-[#2D5A27] text-xs uppercase tracking-[0.22em] font-medium">
                <Trophy size={12} weight="fill" /> Why partners choose us
              </div>
              <div className="grid grid-cols-2 gap-3 mt-5">
                {[
                  { v: "47+", l: "Active jurisdictions" },
                  { v: "10%", l: "Lifetime commission" },
                  { v: "0", l: "Tech setup cost" },
                  { v: "Wkly", l: "Payouts" },
                ].map((k) => (
                  <div key={k.l} className="rounded-xl p-4 bg-white/60 border border-white/50">
                    <div className="font-heading text-2xl tracking-tight text-slate-900">{k.v}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1">{k.l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-white/40 text-[11px] text-slate-600 leading-relaxed">
                Best fit for: LSGD reps · waste-management entrepreneurs · housing-society aggregators · clinic chains.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// =============== Section D — Government transparency ===============
const SectionD = ({ branding }) => (
  <section id="government" className={`${SECTION} bg-slate-50 border-y border-slate-200`} data-testid="section-d">
    <div className="max-w-7xl mx-auto">
      <div className="overflow-hidden border-y border-slate-200 -mx-6 md:-mx-12 lg:-mx-24 mb-14 py-3 bg-white">
        <div className="flex gap-12 animate-[scroll_45s_linear_infinite] whitespace-nowrap text-xs uppercase tracking-[0.22em] text-slate-500">
          {["Trivandrum Corporation · 38 zones live", "12,402 households mapped today", "98.6% scheduled pickup compliance", "8.4 tonnes diverted this month", "BMW chain-of-custody · 100%", "47 govt jurisdictions onboarded"]
            .concat(["Trivandrum Corporation · 38 zones live", "12,402 households mapped today", "98.6% scheduled pickup compliance", "8.4 tonnes diverted this month"])
            .map((s, i) => <span key={i}>{s}</span>)}
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 className={`${H2}`}>{branding?.section_d_title || "Transparency, by default."}</h2>
          <p className={`${LEAD} mt-5 max-w-2xl`}>{branding?.section_d_body || "Read-only dashboards for state, district, and LSGD officials. Drill into ward-level compliance, complaint heat-maps, and waste reports — without ever touching operational data."}</p>
          <Link to="/government" data-testid="section-d-cta" className={`${BTN_GREEN} mt-7`}>
            <BuildingOffice size={16} weight="duotone" /> Government portal
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[{ v: "47", l: "Jurisdictions" }, { v: "1.2M+", l: "Citizens covered" }, { v: "100%", l: "BMW compliance" }, { v: "0", l: "Disposal violations" }]
            .map((k) => (
              <div key={k.l} className="rounded-xl p-5 bg-white border border-slate-200">
                <div className="font-heading text-3xl tracking-tight">{k.v}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-1">{k.l}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
    <style>{`@keyframes scroll { from { transform: translateX(0) } to { transform: translateX(-50%) } }`}</style>
  </section>
);

// =============== Section E — Sustainability ===============
const SectionE = ({ branding }) => (
  <section id="impact" className={`${SECTION} bg-[#1B3B17] text-white relative overflow-hidden`} data-testid="section-e">
    <div className="absolute inset-0 opacity-20"
         style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #E63946 0, transparent 30%), radial-gradient(circle at 80% 60%, #5C9F4F 0, transparent 35%)" }} />
    <div className="max-w-7xl mx-auto relative">
      <div className="text-center mb-14">
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-medium">{branding?.section_e_title || "Every gram, accounted for."}</h2>
        {branding?.section_e_body && <p className="text-slate-300 mt-4 max-w-2xl mx-auto text-sm">{branding.section_e_body}</p>}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {[{ v: parseInt(branding?.kpi_kg || 86420, 10),    suf: "kg", l: "Diverted from landfill", testid: "kpi-counter-kg" },
          { v: parseInt(branding?.kpi_co2 || 240, 10),       suf: "T",  l: "CO₂ saved", testid: "kpi-counter-co2" },
          { v: parseInt(branding?.kpi_customers || 12402, 10), suf: "+", l: "Active customers", testid: "kpi-counter-customers" },
          { v: parseInt(branding?.kpi_zones || 38, 10),      suf: "",   l: "Health zones operational", testid: "kpi-counter-zones" }].map((k, i) => (
          <CountUp key={i} value={k.v} suffix={k.suf} label={k.l} testid={k.testid} />
        ))}
      </div>
    </div>
  </section>
);

const CountUp = ({ value, suffix, label, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let s = 0; const step = value / 60;
    const id = setInterval(() => {
      s += step; if (s >= value) { s = value; clearInterval(id); }
      setN(Math.floor(s));
    }, 22);
    return () => clearInterval(id);
  }, [inView, value]);
  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <div className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-[#E63946]" />
      <div className="font-heading text-5xl sm:text-6xl tracking-tighter font-light">{n.toLocaleString()}{suffix}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2">{label}</div>
    </div>
  );
};

// =============== Section F — Referrals ===============
const SectionF = ({ branding }) => (
  <section className={`${SECTION} bg-white`} data-testid="section-f">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-14">
        <h2 className={`${H2}`}>{branding?.section_f_title || "Earn while you waste less."}</h2>
        {branding?.section_f_body && <p className={`${LEAD} mt-4 max-w-2xl mx-auto`}>{branding.section_f_body}</p>}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <RewardCard tone="green" Icon={Users}
                    kicker={branding?.referral_customer_kicker || "For customers"}
                    title={branding?.referral_customer_title || "10% lifetime affiliate"}
                    desc={branding?.referral_customer_body || "Refer your neighbour. Earn 10% of their first package payment, lifetime — credited instantly to your in-app wallet."}
                    chips={["UPI payout from ₹100", "Auto-credit on payment", "Wallet · ledger · referral link"]}
                    cta={branding?.referral_customer_cta || "Open customer wallet"} to="/me/login" />
        <RewardCard tone="red" Icon={Trophy}
                    kicker={branding?.referral_partner_kicker || "For partners"}
                    title={branding?.referral_partner_title || "Up to 10% partner commission"}
                    desc={branding?.referral_partner_body || "LSGD reps, retailers, service providers — onboard customers via your unique link, earn weekly payouts."}
                    chips={["2% / 5% / 10% tiers", "Public profile page", "Payout history · UTR audit"]}
                    cta={branding?.referral_partner_cta || "Apply as partner"} to="#contact" />
      </div>
    </div>
  </section>
);

const RewardCard = ({ tone, Icon, kicker, title, desc, chips, cta, to }) => {
  const isRed = tone === "red";
  return (
    <div className={`rounded-3xl p-8 lg:p-10 relative overflow-hidden border ${isRed ? "bg-[#FCEEEF] border-[#E63946]/20" : "bg-[#EFF5EE] border-[#2D5A27]/20"}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-medium text-slate-500">
        <Icon size={14} color={isRed ? "#E63946" : "#2D5A27"} weight="fill" /> {kicker}
      </div>
      <div className="font-heading text-3xl tracking-tight font-medium mt-3 mb-3 text-slate-900">{title}</div>
      <div className="text-slate-600 leading-relaxed">{desc}</div>
      <div className="flex flex-wrap gap-2 mt-5">
        {chips.map((c) => (
          <span key={c} className={`text-[11px] px-2.5 py-1 rounded-full bg-white border ${isRed ? "border-[#E63946]/20 text-[#7A1F26]" : "border-[#2D5A27]/20 text-[#1B3B17]"}`}>{c}</span>
        ))}
      </div>
      <Link to={to} className={`mt-7 ${isRed ? BTN_RED : BTN_GREEN}`}>{cta} <ArrowRight size={14} weight="bold" /></Link>
    </div>
  );
};

// =============== Section G — Product store ===============
const SectionG = ({ branding }) => {
  const items = (branding?.store_items && branding.store_items.length) ? branding.store_items : [
    { name: "RED biohazard bag · 5kg", price: 18, tag: "Bestseller" },
    { name: "Sanitary disposal pouch · 500g", price: 12, tag: "Eco" },
    { name: "Bulk packing kit · 10 bags", price: 380, tag: "Saver" },
    { name: "Digital pickup card", price: 0, tag: "Free" },
  ];
  return (
    <section className={`${SECTION} bg-slate-50 border-y border-slate-200`} data-testid="section-g">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <h2 className={`${H2}`}>{branding?.section_g_title || "Supplies, on tap."}</h2>
          </div>
          <Link to="/me/login" className="text-sm text-slate-600 hover:text-slate-900 underline-offset-4 hover:underline">Order from app <ArrowRight size={12} weight="bold" className="inline" /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                        className="rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-xl hover:shadow-green-900/5 hover:-translate-y-1 transition-all">
              <div className="aspect-square rounded-xl bg-gradient-to-br from-[#FCEEEF] to-[#EFF5EE] mb-4 flex items-center justify-center">
                <Storefront size={42} color="#2D5A27" weight="duotone" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#E63946] font-semibold">{p.tag}</span>
              <div className="font-heading text-base mt-1 leading-tight">{p.name}</div>
              <div className="flex items-center justify-between mt-3">
                <span className="font-mono font-semibold">{p.price === 0 ? "Free" : `₹${p.price}`}</span>
                <button className="text-xs text-[#2D5A27] font-semibold hover:underline">Add</button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============== Section H — Testimonials ===============
const SectionH = ({ branding }) => {
  const quotes = (branding?.testimonials && branding.testimonials.length) ? branding.testimonials : [
    { who: "Dr. R. Suresh", role: "Med Director, BMW Hospital", q: "We reduced manual disposal logging by 80%. Audit-ready in one click.", color: "green" },
    { who: "Asha Kumari", role: "Ward Member, Pattom", q: "First time citizens see WHERE their waste goes. Trust is back.", color: "red" },
    { who: "TKM College Trust", role: "Hostel Operations", q: "Bag balance + auto-billing eliminated 3 disputes a month.", color: "green" },
  ];
  return (
    <section className={`${SECTION} bg-white`} data-testid="section-h">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className={`${H2}`}>{branding?.section_h_title || "Trusted by the institutions India relies on."}</h2>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          {quotes.map((q, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                        className={`rounded-3xl p-7 border ${i === 1 ? "lg:translate-y-8" : ""} ${q.color === "red" ? "bg-[#FCEEEF] border-[#E63946]/20" : "bg-[#EFF5EE] border-[#2D5A27]/20"}`}>
              <GraduationCap size={28} color={q.color === "red" ? "#E63946" : "#2D5A27"} weight="duotone" />
              <p className="font-heading text-xl leading-snug mt-4 text-slate-900">"{q.q}"</p>
              <div className="mt-5 text-sm text-slate-700">{q.who}</div>
              <div className="text-xs text-slate-500">{q.role}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============== Section I — Lead capture ===============
const SectionI = ({ branding }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", organisation: "", role: "customer", message: "",
  });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required"); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/leads`, { ...form, source: "homepage" });
      setDone(true);
      setForm({ name: "", phone: "", email: "", organisation: "", role: "customer", message: "" });
      toast.success("Thanks! We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    }
    setBusy(false);
  };
  return (
    <section id="contact" className={`${SECTION} bg-gradient-to-b from-white to-[#F7FAF6]`} data-testid="section-i">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className={`${H2}`}>{branding?.section_i_title || "Build your smart waste stack."}</h2>
          <p className={`${LEAD} mt-5 max-w-md`}>{branding?.section_i_body || "Customer · Institution · Franchise applicant · Government department — drop a line. A specialist replies within 24 hours."}</p>
          <div className="mt-8 space-y-3 text-sm text-slate-700">
            <ContactLine I={EnvelopeSimple} v={branding?.support_email || "support@rescigo.health"} />
            <ContactLine I={Phone} v={branding?.support_phone || "+91 — coming soon"} />
            <ContactLine I={MapPin} v={branding?.company_address || "Trivandrum, Kerala · India"} />
          </div>
          <div className="flex gap-2 mt-6">
            <SocialLink Icon={LinkedinLogo} />
            <SocialLink Icon={InstagramLogo} />
            <SocialLink Icon={TwitterLogo} />
            <SocialLink Icon={YoutubeLogo} />
          </div>
        </div>
        <form onSubmit={submit} data-testid="lead-capture-form" className={`${GLASS} rounded-3xl p-7 lg:p-8 space-y-4`}>
          {done ? (
            <div className="text-center py-12" data-testid="lead-thanks">
              <CheckCircle size={48} color="#2D5A27" weight="fill" className="inline-block mb-3" />
              <div className="font-heading text-2xl">Thank you, {/* anonymous after submit */}</div>
              <div className="text-sm text-slate-600 mt-2">We've received your message. A Rescigo specialist will reach out shortly.</div>
              <button onClick={() => setDone(false)} className="mt-6 text-sm underline text-slate-700">Submit another</button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Full name *" v={form.name} onChange={upd("name")} testid="lead-name" />
                <Field label="Phone *" v={form.phone} onChange={upd("phone")} testid="lead-phone" />
              </div>
              <Field label="Email" type="email" v={form.email} onChange={upd("email")} testid="lead-email" />
              <Field label="Organisation" v={form.organisation} onChange={upd("organisation")} testid="lead-org" />
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium block mb-1.5">I'm a…</label>
                <select value={form.role} onChange={upd("role")} data-testid="lead-role"
                        className="w-full h-11 px-3 border border-slate-200 rounded-xl bg-white text-sm">
                  <option value="customer">Customer / Resident</option>
                  <option value="institution">Institution / Hospital</option>
                  <option value="partner">Partner / Reseller</option>
                  <option value="franchise">Franchise applicant</option>
                  <option value="government">Government / LSGD</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium block mb-1.5">Message</label>
                <textarea value={form.message} onChange={upd("message")} rows={4} data-testid="lead-message"
                          placeholder="Tell us about your zone, scale, or compliance need…"
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white text-sm resize-none" />
              </div>
              <button type="submit" disabled={busy} data-testid="lead-submit"
                      className={`${BTN_GREEN} w-full disabled:opacity-60`}>
                {busy ? "Sending…" : "Send message"} <ArrowRight size={14} weight="bold" />
              </button>
              <div className="text-[10px] text-slate-400 text-center">
                By submitting, you accept our <Link to="/privacy" className="underline">privacy policy</Link> and <Link to="/terms" className="underline">terms</Link>.
              </div>
            </>
          )}
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, type = "text", v, onChange, testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium block mb-1.5">{label}</label>
    <input type={type} value={v} onChange={onChange} data-testid={testid}
           className="w-full h-11 px-3 border border-slate-200 rounded-xl bg-white text-sm focus:outline-none focus:border-[#2D5A27] focus:ring-2 focus:ring-[#2D5A27]/10" />
  </div>
);

const ContactLine = ({ I, v }) => (
  <div className="flex items-center gap-2.5"><I size={14} color="#2D5A27" weight="duotone" /> {v}</div>
);
const SocialLink = ({ Icon }) => (
  <a href="#" className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-[#2D5A27] hover:text-white hover:border-[#2D5A27] transition">
    <Icon size={16} weight="duotone" />
  </a>
);

// =============== Footer ===============
const Footer = ({ branding }) => (
  <footer className="bg-[#050C07] text-slate-300 px-6 md:px-12 lg:px-24 py-16 border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <img src={branding?.logo_white_url || "/brand/rescigo-logo-white.png"} alt="Rescigo Health" className="h-9 w-auto opacity-95 mb-5" />
          <p className="text-sm leading-relaxed max-w-sm text-slate-400">
            {branding?.footer_text || "Not just waste collection. A national-grade smart waste operations ecosystem."}
          </p>
          <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
            <Leaf size={12} weight="fill" color="#5C9F4F" /> ISO 14001 · CPCB · Made in Kerala
          </div>
        </div>
        <FooterCol h="Solutions" items={["For households", "For institutions", "For corporates", "For LSGDs"]} />
        <FooterCol h="Company" items={["Franchise", "Partners", "Government", "Careers"]} />
        <FooterCol h="Sign in" items={[
          { label: "Customer", to: "/me/login" },
          { label: "Government", to: "/government" },
        ]} />
      </div>
      <div className="border-t border-white/5 mt-12 pt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
        <div>© {new Date().getFullYear()} Rescigo Health. All rights reserved.</div>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-slate-300">Privacy</Link>
          <Link to="/terms" className="hover:text-slate-300">Terms</Link>
          <Link to="/refund" className="hover:text-slate-300">Refunds</Link>
        </div>
      </div>
    </div>
  </footer>
);

const FooterCol = ({ h, items }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium mb-4">{h}</div>
    <ul className="space-y-2.5 text-sm">
      {items.map((it, i) => {
        const obj = typeof it === "object" ? it : { label: it };
        return (
          <li key={i}>
            {obj.to ? (
              <Link to={obj.to} className="hover:text-white transition">{obj.label}</Link>
            ) : (
              <a href="#" className="hover:text-white transition">{obj.label}</a>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);
